import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme, CircularProgress, Box } from '@mui/material';
import RegistrationSteps from './components/RegistrationSteps';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import RegistrationSuccess from './components/RegistrationSuccess';
import SimpleForm from './components/SimpleForm';
import SimpleFormSuccess from './components/SimpleFormSuccess';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Amplify } from '@aws-amplify/core';
import awsconfig from './aws-exports';
import { getSubscriptionPlans } from './services/subscription-plans';
import { showPlanStep } from './utils/environment';

Amplify.configure(awsconfig);

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#7F56D9',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7F56D9',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#7F56D9',
          },
        },
      },
    },
  },
});

// Define subscription types and their corresponding endpoints and texts
interface SubscriptionPlan {
  planName: string;
  description: string;
  pricePerMonth: number;
  pricePerYear: number;
}

interface SubscriptionConfig {
  [key: string]: {
    text: string;
    pricePerMonth: number;
    pricePerYear: number;
  };
}

function App() {
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Get subscription type from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const rawSubscriptionType = urlParams.get('type') || 'free';

  const PLAN_TYPE_MAP: Record<string, string> = {
    free: 'Free',
    gestor: 'Gestor',
    autonomo: 'Autónomo (IVA no incluido)',
    'autonomo-contable': 'Autónomo + Acceso al Contable (IVA no incluido)',
    business: 'Business (IVA no incluido)',
    'business-contable': 'Business + Acceso al Contable (IVA no incluido)',
    'business-advance': 'Business Advance (IVA no incluido)',
    'business-advance-contable': 'Business Advance + Acceso al Contable (IVA no incluido)',
    enterprise: 'Enterprise',
  };

  const normalizedTypeKey = rawSubscriptionType.trim().toLowerCase();
  const subscriptionType = PLAN_TYPE_MAP[normalizedTypeKey] || rawSubscriptionType;
  const billingPeriodParam = urlParams.get('period') || 'month';
  const billingPeriod = billingPeriodParam === 'year' ? 'year' : 'month';
  const referredByCode = urlParams.get('ref') || undefined;
  const discountCode = urlParams.get('promo') || undefined;
  // Single use invitation from a gestoría. It carries the discount, so it is
  // never a code the visitor can type: the server resolves it against the
  // email it was sent to.
  const inviteToken = urlParams.get('invite') || undefined;

  // Fetch subscription plans on component mount (only in production mode)
  useEffect(() => {
    // In demo mode, we don't need subscription plans
    if (!showPlanStep) {
      return;
    }

    const fetchSubscriptionPlans = async () => {
      setLoading(true);
      try {
        const plans = await getSubscriptionPlans();
        setSubscriptionPlans(plans);
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionPlans();
  }, [showPlanStep]);

  // Create subscription config based on fetched plans
  const subscriptionConfig: SubscriptionConfig = subscriptionPlans
    .filter((plan) => plan.planName !== 'Agent')
    .reduce((config, plan) => {
      config[plan.planName] = {
        text: plan.description,
        pricePerMonth: plan.pricePerMonth,
        pricePerYear: plan.pricePerYear,
      };
      return config;
    }, {} as SubscriptionConfig);

  // Add default Free plan if not present
  if (!subscriptionConfig.Free) {
    subscriptionConfig.Free = {
      text: 'Free subscription plan',
      pricePerMonth: 0,
      pricePerYear: 0,
    };
  }

  return (
    <ThemeProvider theme={theme}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <RegistrationSteps
                  subscriptionType={subscriptionType}
                  subscriptionPlans={subscriptionPlans}
                  period={billingPeriod}
                  referredByCode={referredByCode}
                  discountCode={discountCode}
                  inviteToken={inviteToken}
                />
              }
            />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/simple-form" element={<SimpleForm />} />
            <Route path="/simple-form/success" element={<SimpleFormSuccess />} />
          </Routes>
        </BrowserRouter>
      )}
    </ThemeProvider>
  );
}

export default App;
